import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import type { Tables } from "@/integrations/supabase/types";
import { queryKeys } from "@/lib/queryKeys";
import { db, type QueryFilter } from "@/lib/supabaseQuery";
import { PLAN_ID_TO_TIER, PLAN_ID_TO_NAME } from "@/constants/membershipTiers";
import { logger } from '@/utils/logger';

type MembershipRow = Pick<Tables<'users_memberships'>,
  | 'plan_id'
  | 'status'
  | 'payment_provider'
  | 'current_period_start'
  | 'current_period_end'
  | 'stripe_customer_id'
  | 'paypal_customer_id'
>;

export type MyMembership = MembershipRow & {
  plan_name: string | null;
  plan_tier: string | null;
  plan_price: number | null;
};

type PlanRow = Pick<Tables<'users_membership_plans'>, 'id' | 'name' | 'tier' | 'price_monthly'>;
type OrderRow = Pick<Tables<'users_order_history'>, 'amount'>;

export function useMyMembership(options?: { refetchInterval?: number | false }) {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...queryKeys.memberships.mine(), user?.id],
    refetchInterval: options?.refetchInterval ?? false,
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return null;

      const memberships = await db.fetchTop<MembershipRow>(
        'users_memberships',
        'plan_id, status, payment_provider, current_period_start, current_period_end, stripe_customer_id, paypal_customer_id',
        1,
        {
          filters: [{ column: 'user_id', operator: 'eq', value: user.id }],
          orderBy: { column: 'created_at', ascending: false },
        }
      );

      const membership = memberships[0] ?? null;
      if (!membership) return null;

      // Use fetchTop(1) instead of fetchOne to avoid .single() which causes
      // statement timeouts on users_membership_plans with RLS.
      // If the plan lookup still times out, keep the membership visible and
      // let the UI fall back to legacy plan mappings from plan_id.
      const planPromise: Promise<PlanRow | null> = membership.plan_id
        ? db.fetchTop<PlanRow>(
            'users_membership_plans',
            'id, name, tier, price_monthly',
            1,
            { filters: [{ column: 'id', operator: 'eq', value: membership.plan_id }] }
          )
            .then(rows => rows[0] ?? null)
            .catch((err) => {
              logger.warn('[useMyMembership] Plan query failed, falling back to PLAN_ID_TO_TIER', {
                plan_id: membership.plan_id,
                error: err?.message ?? String(err),
              });
              return null;
            })
        : Promise.resolve(null);

      const orderFilters: QueryFilter[] = [
        { column: 'user_id', operator: 'eq', value: user.id },
        { column: 'payment_status', operator: 'eq', value: 'success' },
      ];
      if (membership.plan_id) {
        orderFilters.push({ column: 'membership_plan_id', operator: 'eq', value: membership.plan_id });
      }

      const orderPromise = db.fetchTop<OrderRow>(
        'users_order_history',
        'amount',
        1,
        {
          filters: orderFilters,
          orderBy: { column: 'order_date', ascending: false },
        }
      ).catch(() => [] as OrderRow[]);

      const [plan, orders] = await Promise.all([planPromise, orderPromise]);

      return {
        ...membership,
        plan_name: plan?.name ?? (membership.plan_id ? PLAN_ID_TO_NAME[membership.plan_id] : null) ?? null,
        plan_tier: plan?.tier
          ?? (membership.plan_id ? PLAN_ID_TO_TIER[membership.plan_id] : null)
          ?? (membership.plan_id ? 'professional' : null),
        plan_price: orders[0]?.amount ?? plan?.price_monthly ?? null,
      } satisfies MyMembership;
    },
  });
}
